import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { Progress } from "Components/Progress/Progress";
import { Button } from "Components/Button/Button";
import { CheckmarkIcon } from "Components/Icons/Arrows/CheckmarkIcon";
import { CircularProgress } from "@mui/material";
import "./Survey.scss";

export const Survey = ({
  surveyData,
  completed = false,
  onCompleteSurvey,
  isLoading = false,
  className = "",
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const questions = React.useMemo(
    () => surveyData?.preguntas || [],
    [surveyData]
  );
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const isFirstQuestion = currentQuestionIndex === 0;
  const hasAnsweredCurrent =
    currentQuestion && answers[currentQuestion.idPregunta]?.selectedOption;

  useEffect(() => {
    if (completed && totalQuestions > 0) {
      setCurrentQuestionIndex(totalQuestions - 1);
    }
  }, [completed, totalQuestions, questions]);

  if (!questions.length) return null;

  const handleNext = () => {
    if (isLastQuestion) {
      onCompleteSurvey?.(answers);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  const handleAnswerSelect = (questionId, answerId) => {
    if (completed) return;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: answerId,
      },
    }));
  };

  const handleTextChange = (e) => {
    if (completed) return;

    const value = e.target.value;
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.idPregunta]: {
        ...prev[currentQuestion.idPregunta],
        textAnswer: value,
      },
    }));
  };

  const isOptionSelected = (optionId) =>
    answers[currentQuestion?.idPregunta]?.selectedOption === optionId;

  return (
    <div className={clsx("survey", className)}>
      <div className="survey__header">
        <h2 className="survey__title">Encuesta de satisfacción</h2>
        <div className="survey__divider" />
      </div>

      <div className="survey__progress-section">
        <p className="survey__question-text">{currentQuestion.pregunta}</p>
        <Progress
          variant="fraction"
          current={currentQuestionIndex + 1}
          total={totalQuestions}
        />
      </div>

      {currentQuestion && (
        <div className="survey__options">
          {currentQuestion.respuestas?.map((respuesta) => {
            const isSelected = isOptionSelected(respuesta.idRespuesta);

            return (
              <div
                key={respuesta.idRespuesta}
                className={clsx(
                  "survey__option",
                  isSelected && "survey__option--selected",
                  completed && "survey__option--disabled"
                )}
                onClick={() =>
                  handleAnswerSelect(
                    currentQuestion.idPregunta,
                    respuesta.idRespuesta
                  )
                }
              >
                <p className="survey__option-label">{respuesta.titulo}</p>
                <div
                  className={clsx(
                    "survey__option-indicator",
                    isSelected && "survey__option-indicator--selected"
                  )}
                >
                  <span
                    className={clsx(
                      "survey__option-circle",
                      isSelected && "survey__option-circle--selected"
                    )}
                  >
                    {isSelected && <CheckmarkIcon />}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="survey__input">
        <label className="survey__textarea-label">
          Indícanos ¿por qué? (opcional)
        </label>
        <textarea
          placeholder="Escribe aquí tu respuesta"
          className="survey__textarea"
          value={answers[currentQuestion?.idPregunta]?.textAnswer || ""}
          onChange={handleTextChange}
          disabled={completed}
        />
      </div>

      <div className="survey__navigation">
        <Button
          onClick={handleBack}
          disabled={isFirstQuestion || completed || isLoading}
          className="survey__nav-button"
          variant="secundario-cta"
        >
          REGRESAR
        </Button>

        <Button
          onClick={handleNext}
          disabled={!hasAnsweredCurrent || completed || isLoading}
          className="survey__nav-button"
        >
          {isLoading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
          {isLastQuestion ? "FINALIZAR" : "SIGUIENTE"}
        </Button>
      </div>
    </div>
  );
};
